import RhfMaskedPhoneField from "@/components/Forms/RhfMaskedPhoneField/RhfMaskedPhoneField.tsx";
import { stateAbbreviations } from "@/constants/stateAbbreviations.ts";
import { Button, Grid2 as Grid, Link, MenuItem, Paper, Typography } from "@mui/material";
import { PhoneIcon } from "lucide-react";
import { RhfTextField } from "mui-rhf-integration";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

type Props = {
    onSaveProvider?: () => void;
    onCancelAddProvider?: () => void;
    isAddingProvider?: boolean;
};

const CompanyAddressInformation = ({
    onSaveProvider,
    onCancelAddProvider,
    isAddingProvider = false,
}: Props) => {
    const form = useFormContext();
    const [isEditing, setIsEditing] = useState(false);
    const [originalValues, setOriginalValues] = useState({
        phoneNumber: "",
        phoneExtension: "",
        companyName: "",
        address: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
    });

    const companyId = form.watch("idCompanyID");

    const handleEditClick = () => {
        if (isEditing) {
            form.reset({
                ...form.getValues(),
                phoneNumber: originalValues.phoneNumber,
                phoneExtension: originalValues.phoneExtension,
                companyName: originalValues.companyName,
                address: originalValues.address,
                address2: originalValues.address2,
                city: originalValues.city,
                state: originalValues.state,
                zip: originalValues.zip,
            });
        } else {
            const currentValues = form.getValues();
            setOriginalValues({
                phoneNumber: currentValues.phoneNumber,
                phoneExtension: currentValues.phoneExtension,
                companyName: currentValues.companyName,
                address: currentValues.address,
                address2: currentValues.address2,
                city: currentValues.city,
                state: currentValues.state,
                zip: currentValues.zip,
            });
        }
        setIsEditing(!isEditing);
    };

    const handleSaveProvider = () => {
        if (onSaveProvider) {
            onSaveProvider();
        }
    };

    const handleCancelAddProvider = () => {
        if (onCancelAddProvider) {
            onCancelAddProvider();
        }
    };

    if (!(companyId || isAddingProvider)) {
        return null;
    }

    const fieldsEditable = isEditing || isAddingProvider;

    return (
        <Grid container>
            <Grid size={12}>
                <Paper
                    sx={{
                        pt: 1,
                        backgroundColor: "#eee",
                    }}
                >
                    <Grid container sx={{ px: 2, pb: 1 }}>
                        <Grid size={6}>
                            <Typography variant="h6" sx={{ color: "#666" }}>
                                {isAddingProvider ? "New Provider Address" : "Address"}
                            </Typography>
                        </Grid>
                        <Grid size={6} sx={{ textAlign: "right" }}>
                            {!isAddingProvider && (
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={handleEditClick}
                                    sx={{
                                        color: "#4CAF50",
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                    }}
                                >
                                    {isEditing ? "Cancel Edit" : "Edit Company Address"}
                                </Link>
                            )}
                            {isAddingProvider && (
                                <Grid container spacing={1} sx={{ justifyContent: "flex-end" }}>
                                    <Grid>
                                        <Button
                                            size="small"
                                            onClick={handleCancelAddProvider}
                                            sx={{
                                                fontSize: "12px",
                                                minWidth: "auto",
                                                px: 1,
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handleSaveProvider}
                                            sx={{
                                                fontSize: "12px",
                                                minWidth: "auto",
                                                px: 1,
                                            }}
                                        >
                                            Save Provider
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        sx={{
                            borderRadius: "8px",
                            p: 1,
                        }}
                    >
                        <Grid size={12} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"companyName"}
                                label={"Company Name"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={9} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"address"}
                                label={"Address"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={3} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"address2"}
                                label={"Apt/Suite"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={6} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"city"}
                                label={"City"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={2} sx={{ p: 1 }}>
                            <RhfTextField
                                select
                                control={form.control}
                                name={"state"}
                                label={"State"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                }}
                            >
                                {stateAbbreviations.map((type) => (
                                    <MenuItem key={`state-${type}`} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </RhfTextField>
                        </Grid>
                        <Grid size={4} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"zip"}
                                label={"Zip"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={9} sx={{ p: 1 }}>
                            <RhfMaskedPhoneField
                                name="phoneNumber"
                                label="Phone Number"
                                required
                                startAdornment={<PhoneIcon />}
                                disabled={!fieldsEditable}
                            />
                        </Grid>
                        <Grid size={3} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"phoneExtension"}
                                label={"Ext"}
                                sx={{
                                    width: "100%",
                                }}
                                disabled={!fieldsEditable}
                            />
                        </Grid>
                        <Grid size={12} sx={{ p: 1 }}>
                            <RhfMaskedPhoneField
                                name="faxNumber"
                                label="Fax Number"
                                required
                                startAdornment={<PhoneIcon />}
                                disabled={!fieldsEditable}
                            />
                        </Grid>
                        <Grid size={12} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name="email"
                                label="Email Address"
                                required
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default CompanyAddressInformation;
